import { useRef, useEffect, useState, useCallback } from 'react';
import { useSandboxRunner } from './SandboxRunnerContext';
import { TestResultsPanel } from './TestResultsPanel';
import './PreviewPanel.css';

export interface PreviewPanelHandle {
  getGridContainer: () => HTMLDivElement | null;
}

const HEADER_HEIGHT = 24; // Toolbar height in pixels

/**
 * Resizable vertical divider hook for the console panel
 */
function useConsoleResizer(
  previewContentRef: React.RefObject<HTMLDivElement | null>,
  consoleRef: React.RefObject<HTMLDivElement | null>,
) {
  const resizerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const resizer = resizerRef.current;
    const previewContent = previewContentRef.current;
    const consoleEl = consoleRef.current;
    if (!resizer || !previewContent || !consoleEl) return;

    const panelEl = previewContent.closest('.preview-panel') as HTMLElement | null;
    if (!panelEl) return;

    let isDragging = false;

    const handleMouseDown = (e: MouseEvent) => {
      isDragging = true;
      resizer.classList.add('active');
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
      e.preventDefault();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      const panelRect = panelEl.getBoundingClientRect();
      // Account for header height
      const headerEl = panelEl.querySelector('.preview-header') as HTMLElement | null;
      const headerHeight = headerEl ? headerEl.offsetHeight : 0;
      const resizerHeight = resizer.offsetHeight;

      const availableHeight = panelRect.height - headerHeight - resizerHeight;
      const mouseYLocal = e.clientY - panelRect.top - headerHeight;

      const previewPercent = Math.max(15, Math.min(85, (mouseYLocal / availableHeight) * 100));
      const consolePercent = 100 - previewPercent;

      previewContent.style.flex = `${previewPercent} 1 0%`;
      consoleEl.style.flex = `${consolePercent} 1 0%`;
      consoleEl.style.height = 'auto';
      consoleEl.style.maxHeight = 'none';
      consoleEl.style.minHeight = '40px';
    };

    const handleMouseUp = () => {
      if (isDragging) {
        isDragging = false;
        resizer.classList.remove('active');
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        window.dispatchEvent(new Event('resize'));
      }
    };

    resizer.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      resizer.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [previewContentRef, consoleRef]);

  return resizerRef;
}

/**
 * Calculate optimal grid layout for square (1:1 aspect ratio) client canvases.
 * Matches the original SandboxRunner resize logic.
 */
function calculateOptimalGrid(
  clientCount: number,
  containerWidth: number,
  containerHeight: number
): { cols: number; rows: number; cellWidth: number; cellHeight: number } {
  if (clientCount === 0 || containerWidth <= 0 || containerHeight <= 0) {
    return { cols: 1, rows: 1, cellWidth: 300, cellHeight: 300 + HEADER_HEIGHT };
  }

  const gap = 2;
  let bestSize = 0;
  let bestCols = 1;
  let bestRows = 1;

  // Find the grid configuration that maximizes canvas size
  for (let c = 1; c <= clientCount; c++) {
    const r = Math.ceil(clientCount / c);
    const availW = containerWidth - (c - 1) * gap;
    const availH = containerHeight - (r - 1) * gap;

    // Size such that canvas fits in width and canvas + header fits in height
    const s = Math.min(availW / c, (availH / r) - HEADER_HEIGHT);

    if (s > bestSize) {
      bestSize = s;
      bestCols = c;
      bestRows = r;
    }
  }

  // Safety check
  if (bestSize < 1) bestSize = 1;

  const cellWidth = Math.floor(bestSize);
  const cellHeight = Math.floor(bestSize + HEADER_HEIGHT);

  return { cols: bestCols, rows: bestRows, cellWidth, cellHeight };
}

export function PreviewPanel() {
    const {
      clients,
      focusedClientId,
      status,
      statusText,
      isLoading,
      focusClient,
      removeClient,
      addClient,
      updateClientSettings,
    } = useSandboxRunner();

    const gridRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const consoleRef = useRef<HTMLDivElement>(null);
    const [gridLayout, setGridLayout] = useState({ cols: 1, rows: 1, cellWidth: 300, cellHeight: 324 });
    const consoleResizerRef = useConsoleResizer(containerRef, consoleRef);

    // Recalculate grid layout when clients change or container resizes
    const updateLayout = useCallback(() => {
      const container = containerRef.current;
      if (!container) return;

      const { clientWidth, clientHeight } = container;
      const layout = calculateOptimalGrid(clients.length, clientWidth, clientHeight);
      setGridLayout(layout);
    }, [clients.length]);

    // Update layout on mount, client count change, and window resize
    useEffect(() => {
      updateLayout();

      const handleResize = () => updateLayout();
      window.addEventListener('resize', handleResize);
      
      // Use ResizeObserver for more accurate container size tracking
      const container = containerRef.current;
      let resizeObserver: ResizeObserver | null = null;
      if (container && typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(container);
      }

      return () => {
        window.removeEventListener('resize', handleResize);
        resizeObserver?.disconnect();
      };
    }, [updateLayout]);

    // Attach the canvas containers to the grid when clients change
    useEffect(() => {
      const gridContainer = gridRef.current;
      if (!gridContainer) return;

      // Clear grid and re-add all client containers
      // This ensures proper ordering and handles additions/removals
      const existingChildren = new Set<HTMLElement>();
      clients.forEach(client => existingChildren.add(client.container));

      // Remove children that are no longer in clients
      Array.from(gridContainer.children).forEach(child => {
        if (!existingChildren.has(child as HTMLElement)) {
          gridContainer.removeChild(child);
        }
      });

      // Add new children in order
      clients.forEach((client, index) => {
        const container = client.container;
        
        // Update focus styling
        const isFocused = focusedClientId === client.id;
        container.classList.toggle('focused', isFocused);
        
        // Add toolbar if not present - only create once, then update styling
        let toolbar = container.querySelector('.sandbox-client-toolbar') as HTMLElement | null;
        if (!toolbar) {
          toolbar = createToolbar(
            client.id,
            isFocused,
            () => focusClient(client.id),
            () => removeClient(client.id),
            () => client.instance,
            () => client.settings,
            (ping) => updateClientSettings(client.id, { simulatedPing: ping }),
            (peerPing) => updateClientSettings(client.id, { simulatedPeerPing: peerPing })
          );
          container.insertBefore(toolbar, container.firstChild);
        } else {
          // Update focus styling on existing toolbar without recreating
          toolbar.style.backgroundColor = isFocused ? '#1e6f5c' : '#222';
          toolbar.style.borderBottom = isFocused ? '2px solid #4ec9b0' : '1px solid #444';
        }
        
        // Add click handler for focusing
        if (!container.dataset.hasClickHandler) {
          container.addEventListener('click', (e) => {
            if ((e.target as HTMLElement).closest('.sandbox-client-toolbar div')) return;
            focusClient(client.id);
          });
          container.dataset.hasClickHandler = 'true';
        }

        // Add to grid if not already present
        if (container.parentElement !== gridContainer) {
          gridContainer.appendChild(container);
        }
      });
    }, [clients, focusedClientId, focusClient, removeClient]);

    // Build grid styles from calculated layout
    const gridStyles: React.CSSProperties = {
      display: 'grid',
      gridTemplateColumns: `repeat(${gridLayout.cols}, ${gridLayout.cellWidth}px)`,
      gridTemplateRows: `repeat(${gridLayout.rows}, ${gridLayout.cellHeight}px)`,
      gap: '2px',
      justifyContent: 'center',
      alignContent: 'center',
    };

    return (
      <div className="preview-panel">
        <div className="preview-header">
          <h3>Live Preview</h3>
          <button
            id="add-client-btn"
            className="add-client-btn"
            onClick={() => addClient()}
            disabled={status !== 'running'}
            title="Add Client"
          >
            + Add Client
          </button>
        </div>
        <div id="canvas-container" ref={containerRef} className="preview-content">
          {isLoading && (
            <div className="loading-overlay">
              <div className="loading-spinner" />
              <span>{statusText}</span>
            </div>
          )}
          <div
            ref={gridRef}
            className="sandbox-grid-container"
            style={gridStyles}
          />
        </div>
        <div ref={consoleResizerRef} className="gutter-horizontal" id="console-resizer" />
        <div ref={consoleRef} className="console-panel" id="console-output" />
        <TestResultsPanel />
      </div>
    );
}

/**
 * Create a toolbar element for a client - matches original SandboxRunner styling exactly
 */
function createToolbar(
  clientId: string,
  isFocused: boolean,
  onFocus: () => void,
  onRemove: () => void,
  getClientInstance: () => any,
  getClientSettings: () => { simulatedPing?: number; simulatedPeerPing?: number | null } | undefined,
  onPingChange: (ping: number) => void,
  onPeerPingChange: (peerPing: number) => void
): HTMLElement {
  const toolbar = document.createElement('div');
  toolbar.className = 'sandbox-client-toolbar';
  
  // Apply inline styles exactly like original SandboxRunner
  toolbar.style.height = '24px';
  toolbar.style.flexShrink = '0';
  toolbar.style.backgroundColor = isFocused ? '#1e6f5c' : '#222';
  toolbar.style.color = '#eee';
  toolbar.style.display = 'flex';
  toolbar.style.alignItems = 'center';
  toolbar.style.justifyContent = 'space-between';
  toolbar.style.padding = '0 8px';
  toolbar.style.fontSize = '12px';
  toolbar.style.fontFamily = 'monospace';
  toolbar.style.boxSizing = 'border-box';
  toolbar.style.borderBottom = isFocused ? '2px solid #4ec9b0' : '1px solid #444';
  toolbar.style.cursor = 'pointer';

  // Client ID label
  const clientLabel = document.createElement('span');
  clientLabel.textContent = clientId;
  toolbar.appendChild(clientLabel);

  // Main container for controls
  const controlsContainer = document.createElement('div');
  controlsContainer.style.display = 'flex';
  controlsContainer.style.alignItems = 'center';
  controlsContainer.style.gap = '8px';

  // Settings Dropdown
  const settingsWrapper = document.createElement('div');
  settingsWrapper.style.position = 'relative';

  const settingsBtn = document.createElement('div');
  settingsBtn.style.cursor = 'pointer';
  settingsBtn.style.color = '#fff';
  settingsBtn.style.fontSize = '12px';
  settingsBtn.style.display = 'flex';
  settingsBtn.style.alignItems = 'center';
  settingsBtn.title = 'Settings';
  settingsBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" style="width: 12px; height: 12px; fill: white;"><path d="M259.1 73.5C262.1 58.7 275.2 48 290.4 48L350.2 48C365.4 48 378.5 58.7 381.5 73.5L396 143.5C410.1 149.5 423.3 157.2 435.3 166.3L503.1 143.8C517.5 139 533.3 145 540.9 158.2L570.8 210C578.4 223.2 575.7 239.8 564.3 249.9L511 297.3C511.9 304.7 512.3 312.3 512.3 320C512.3 327.7 511.8 335.3 511 342.7L564.4 390.2C575.8 400.3 578.4 417 570.9 430.1L541 481.9C533.4 495 517.6 501.1 503.2 496.3L435.4 473.8C423.3 482.9 410.1 490.5 396.1 496.6L381.7 566.5C378.6 581.4 365.5 592 350.4 592L290.6 592C275.4 592 262.3 581.3 259.3 566.5L244.9 496.6C230.8 490.6 217.7 482.9 205.6 473.8L137.5 496.3C123.1 501.1 107.3 495.1 99.7 481.9L69.8 430.1C62.2 416.9 64.9 400.3 76.3 390.2L129.7 342.7C128.8 335.3 128.4 327.7 128.4 320C128.4 312.3 128.9 304.7 129.7 297.3L76.3 249.8C64.9 239.7 62.3 223 69.8 209.9L99.7 158.1C107.3 144.9 123.1 138.9 137.5 143.7L205.3 166.2C217.4 157.1 230.6 149.5 244.6 143.4L259.1 73.5zM320.3 400C364.5 399.8 400.2 363.9 400 319.7C399.8 275.5 363.9 239.8 319.7 240C275.5 240.2 239.8 276.1 240 320.3C240.2 364.5 276.1 400.2 320.3 400z"/></svg>';

  const dropdown = document.createElement('div');
  dropdown.style.position = 'absolute';
  dropdown.style.top = '100%';
  dropdown.style.right = '0';
  dropdown.style.backgroundColor = '#222';
  dropdown.style.border = '1px solid #444';
  dropdown.style.padding = '8px';
  dropdown.style.borderRadius = '4px';
  dropdown.style.display = 'none';
  dropdown.style.zIndex = '1000';
  dropdown.style.minWidth = '150px';
  dropdown.style.color = '#eee';
  dropdown.style.boxShadow = '0 2px 8px rgba(0,0,0,0.5)';

  // Ping Control
  const pingControl = document.createElement('div');
  pingControl.style.display = 'flex';
  pingControl.style.flexDirection = 'column';
  pingControl.style.gap = '4px';

  const pingLabel = document.createElement('label');
  pingLabel.textContent = 'Simulated Ping (ms):';
  pingLabel.style.fontSize = '10px';
  pingLabel.style.color = '#aaa';

  const pingInput = document.createElement('input');
  pingInput.type = 'number';
  pingInput.min = '0';
  pingInput.max = '500';
  pingInput.value = '50';
  pingInput.style.backgroundColor = '#333';
  pingInput.style.color = '#fff';
  pingInput.style.border = '1px solid #555';
  pingInput.style.padding = '2px 4px';
  pingInput.style.fontSize = '11px';
  pingInput.style.width = '100%';

  // Update client state on change - sync to runtime settings and mock latency
  pingInput.addEventListener('input', (e) => {
    const val = parseInt((e.target as HTMLInputElement).value) || 0;
    onPingChange(val);
    console.log(`[Sandbox] Client ${clientId} simulated latency set to ${val}ms`);
  });

  // Prevent keyboard events from bubbling to game input systems
  pingInput.addEventListener('keydown', (e) => e.stopPropagation());
  pingInput.addEventListener('keyup', (e) => e.stopPropagation());
  pingInput.addEventListener('click', (e) => e.stopPropagation());
  dropdown.addEventListener('click', (e) => e.stopPropagation());

  pingControl.appendChild(pingLabel);
  pingControl.appendChild(pingInput);
  dropdown.appendChild(pingControl);

  // Peer Ping Control — only shown when this client currently has peer connections
  const peerPingControl = document.createElement('div');
  peerPingControl.style.display = 'none';
  peerPingControl.style.flexDirection = 'column';
  peerPingControl.style.gap = '4px';
  peerPingControl.style.marginTop = '8px';

  const peerPingLabel = document.createElement('label');
  peerPingLabel.textContent = 'Simulated Peer Ping (ms):';
  peerPingLabel.style.fontSize = '10px';
  peerPingLabel.style.color = '#aaa';

  const peerPingInput = document.createElement('input');
  peerPingInput.type = 'number';
  peerPingInput.min = '0';
  peerPingInput.max = '500';
  peerPingInput.value = '50';
  peerPingInput.style.backgroundColor = '#333';
  peerPingInput.style.color = '#fff';
  peerPingInput.style.border = '1px solid #555';
  peerPingInput.style.padding = '2px 4px';
  peerPingInput.style.fontSize = '11px';
  peerPingInput.style.width = '100%';

  peerPingInput.addEventListener('input', (e) => {
    const val = parseInt((e.target as HTMLInputElement).value) || 0;
    onPeerPingChange(val);
    console.log(`[Sandbox] Client ${clientId} simulated peer latency set to ${val}ms`);
  });
  peerPingInput.addEventListener('keydown', (e) => e.stopPropagation());
  peerPingInput.addEventListener('keyup', (e) => e.stopPropagation());
  peerPingInput.addEventListener('click', (e) => e.stopPropagation());

  peerPingControl.appendChild(peerPingLabel);
  peerPingControl.appendChild(peerPingInput);
  dropdown.appendChild(peerPingControl);

  settingsBtn.onclick = (e) => {
    e.stopPropagation();
    const isVisible = dropdown.style.display === 'block';
    dropdown.style.display = isVisible ? 'none' : 'block';
    // Initialize value from current client when opening
    if (!isVisible) {
      const instance = getClientInstance();
      const settings = getClientSettings();
      const ping = settings?.simulatedPing ?? (instance as any)?.simulatedLatency ?? 50;
      pingInput.value = String(ping);

      // Peer ping is only relevant once this client has ≥1 peer connection
      const peerCount = (instance as any)?.connectedPeers?.length ?? 0;
      if (peerCount > 0) {
        peerPingControl.style.display = 'flex';
        // Show the EFFECTIVE peer ping: explicit override → inherits server ping
        const explicit = typeof settings?.simulatedPeerPing === 'number' ? settings.simulatedPeerPing : undefined;
        const effectivePeerPing =
          explicit ?? (instance as any)?.simulatedPeerLatency ?? (instance as any)?.simulatedLatency ?? 50;
        peerPingInput.value = String(effectivePeerPing);
      } else {
        peerPingControl.style.display = 'none';
      }
    }
  };

  // Close dropdown when clicking outside
  document.addEventListener('click', () => {
    dropdown.style.display = 'none';
  });

  settingsWrapper.appendChild(settingsBtn);
  settingsWrapper.appendChild(dropdown);
  controlsContainer.appendChild(settingsWrapper);

  // Close button
  const closeBtn = document.createElement('div');
  closeBtn.textContent = '✕';
  closeBtn.title = 'Remove Client';
  closeBtn.style.cursor = 'pointer';
  closeBtn.style.color = '#ff6b6b';
  closeBtn.style.fontWeight = 'bold';
  closeBtn.style.userSelect = 'none';
  closeBtn.onclick = (e) => {
    e.stopPropagation();
    onRemove();
  };

  controlsContainer.appendChild(closeBtn);
  toolbar.appendChild(controlsContainer);

  return toolbar;
}
